<template>
  <v-dialog
    :value="value"
    :max-width="maxWidth"
    persistent
    :content-class="dialogContentClass"
    @input="$emit('input', $event)">
    <div class="ds-wizard" :class="shellClass" :style="shellStyle">
      <!-- Left rail: brand + steps + view switch -->
      <aside class="ds-wizard__rail">
        <div class="ds-wizard__brand">
          <div class="ds-wizard__brand-icon">
            <v-icon color="white" size="24">{{ icon }}</v-icon>
          </div>
          <div class="ds-wizard__brand-text">
            <span class="ds-wizard__brand-title">{{ title }}</span>
            <span class="ds-wizard__brand-subtitle" :title="subtitle">{{ subtitle }}</span>
          </div>
        </div>

        <nav class="ds-wizard__nav" aria-label="Wizard steps">
          <component
            :is="isStepClickable(i + 1) ? 'button' : 'div'"
            v-for="(step, i) in steps"
            :key="i"
            :type="isStepClickable(i + 1) ? 'button' : null"
            class="ds-wizard__step"
            :class="[stepClass(i + 1), { 'ds-wizard__step--clickable': isStepClickable(i + 1) }]"
            :aria-current="currentStep === i + 1 ? 'step' : null"
            :aria-label="isStepClickable(i + 1) ? `Back to ${step.title}` : null"
            @click="isStepClickable(i + 1) && $emit('step-click', i + 1)">
            <div class="ds-wizard__step-circle">
              <v-icon v-if="isStepComplete(i + 1)" size="16" color="white">mdi-check-bold</v-icon>
              <span v-else>{{ i + 1 }}</span>
            </div>
            <div class="ds-wizard__step-text">
              <span class="ds-wizard__step-title">{{ step.title }}</span>
              <span class="ds-wizard__step-desc">{{ step.description }}</span>
            </div>
            <v-icon
              v-if="currentStep === i + 1 && !isStepComplete(i + 1) && stepVariant !== 'pill'"
              size="16"
              color="white"
              class="ds-wizard__step-arrow">mdi-chevron-right</v-icon>
          </component>
        </nav>

        <!-- Optional rail extras (summary box, info rows) — sits above the view switch -->
        <div v-if="$slots['sidebar-extras']" class="ds-wizard__extras">
          <slot name="sidebar-extras"></slot>
        </div>

        <!-- Rail view-switch (e.g. Config / Preview) — only when the consumer passes ≥2 views -->
        <div v-if="views.length >= 2" class="ds-wizard__viewswitch-wrap">
          <div class="ds-wizard__viewswitch" role="group" aria-label="View mode">
            <button
              v-for="view in views"
              :key="view.key"
              type="button"
              class="ds-wizard__viewbtn"
              :class="{ 'ds-wizard__viewbtn--active': view.key === activeView }"
              :aria-pressed="view.key === activeView"
              :disabled="!!view.disabled"
              :title="view.title || null"
              @click="$emit('update:activeView', view.key)">
              <v-icon size="16">{{ view.icon }}</v-icon>
              <span>{{ view.label }}</span>
            </button>
          </div>
        </div>

        <!-- Optional rail progress bar (0–100) -->
        <div v-if="hasProgress" class="ds-wizard__progress">
          <div class="ds-wizard__progress-row">
            <span>Progress</span>
            <span class="ds-wizard__progress-value">{{ progressValue }}%</span>
          </div>
          <div
            class="ds-wizard__progress-track"
            role="progressbar"
            aria-label="Wizard progress"
            :aria-valuenow="progressValue"
            aria-valuemin="0"
            aria-valuemax="100">
            <div class="ds-wizard__progress-fill" :style="{ transform: `scaleX(${progressValue / 100})` }"></div>
          </div>
        </div>
      </aside>

      <!-- Main content pane -->
      <main class="ds-wizard__main">
        <header class="ds-wizard__header">
          <div class="ds-wizard__header-text">
            <span class="ds-wizard__header-title">{{ currentHeading }}</span>
            <span v-if="currentSubheading" class="ds-wizard__header-subtitle">{{ currentSubheading }}</span>
          </div>
          <v-btn
            icon
            small
            class="ds-wizard__close"
            :disabled="closeDisabled"
            :aria-label="closeLabel"
            :title="closeDisabled && closeDisabledTitle ? closeDisabledTitle : closeLabel"
            @click="$emit('close')">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </header>

        <div class="ds-wizard__content" :class="{ 'ds-wizard__content--flush': flush }">
          <slot :current-step="currentStep"></slot>
        </div>

        <div v-if="$slots.footer" class="ds-wizard__footer">
          <slot name="footer"></slot>
        </div>
      </main>
    </div>
  </v-dialog>
</template>

<script>
/**
 * WizardShell — reusable two-pane wizard chrome (left rail + content + footer),
 * generalized from the Master Import wizard and built on design-system tokens.
 *
 * Usage:
 *   <wizard-shell
 *     :value="dialog" @input="dialog = $event" @close="close"
 *     icon="mdi-view-dashboard-plus-outline"
 *     title="Create Widget" subtitle="Build a dashboard widget"
 *     :steps="[{ title: 'Data Source', description: 'Pick a source' }, ...]"
 *     :current-step="step">
 *     <!-- step content (right pane) -->
 *     <template #footer> ...buttons... </template>
 *   </wizard-shell>
 *
 * Optional (all default off, so existing consumers render unchanged):
 *   #sidebar-extras slot (rail summary / info rows), `progress` (rail bar), `close-disabled`
 *   (+ `close-disabled-title`), `finished`, `flush`, `height`, `dialog-class`, `step-variant="pill"`,
 *   `clickable-steps` (+ `step-click` event), and `views[].disabled` / `views[].title`.
 */
export default {
  name: 'WizardShell',

  props: {
    /** v-model: dialog open state */
    value: { type: Boolean, default: false },
    /** Brand icon (mdi) shown in the rail header */
    icon: { type: String, default: 'mdi-tune-variant' },
    /** Rail title / subtitle */
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    /** Steps: [{ title, description }] */
    steps: { type: Array, required: true },
    /** 1-based current step */
    currentStep: { type: Number, required: true },
    /** Dialog width */
    maxWidth: { type: [String, Number], default: '1180px' },
    /** Accessible name for the icon-only close action. */
    closeLabel: { type: String, default: 'Close wizard' },
    /** Optional rail view-switch options: [{ key, label, icon }]. Rendered when length ≥ 2. */
    views: { type: Array, default: () => [] },
    /** Active view key; pairs with the `update:activeView` event (`.sync`-friendly). */
    activeView: { type: String, default: '' },

    // ---- Optional additions (defaults keep existing consumers unchanged) ----
    /** Disable the header close button, e.g. while work is in flight. */
    closeDisabled: { type: Boolean, default: false },
    /** Tooltip for the close button while it is disabled. */
    closeDisabledTitle: { type: String, default: '' },
    /** Rail progress bar value 0–100; null hides the bar. */
    progress: { type: Number, default: null },
    /** Mark every step up to and including the current one as complete (a finished wizard). */
    finished: { type: Boolean, default: false },
    /** Content pane without padding or own scroll; the consumer lays out and scrolls its content. */
    flush: { type: Boolean, default: false },
    /** CSS height for the shell (e.g. 'min(860px, 90vh)'); empty keeps the default 88vh. */
    height: { type: String, default: '' },
    /** Extra class for the teleported dialog content (lets a consumer scope its own styles). */
    dialogClass: { type: String, default: '' },
    /** Rail step look: 'bar' (default, accent bar) or 'pill' (Append Data / Task Import style). */
    stepVariant: {
      type: String,
      default: 'bar',
      validator: (value) => ['bar', 'pill'].includes(value),
    },
    /** Completed steps become buttons that emit `step-click` (1-based step) — e.g. "Back to Map Fields". */
    clickableSteps: { type: Boolean, default: false },
  },

  computed: {
    dialogContentClass() {
      return this.dialogClass ? `ds-wizard-dialog ${this.dialogClass}` : 'ds-wizard-dialog';
    },
    shellClass() {
      return { 'ds-wizard--pill': this.stepVariant === 'pill' };
    },
    shellStyle() {
      return this.height ? { height: this.height } : null;
    },
    hasProgress() {
      return typeof this.progress === 'number' && !Number.isNaN(this.progress);
    },
    progressValue() {
      return Math.max(0, Math.min(100, Math.round(this.progress || 0)));
    },
    currentStepInfo() {
      return this.steps[this.currentStep - 1] || {};
    },
    currentHeading() {
      return this.currentStepInfo.heading || this.currentStepInfo.title || '';
    },
    currentSubheading() {
      return this.currentStepInfo.subheading || this.currentStepInfo.description || '';
    },
  },

  methods: {
    isStepComplete(stepNumber) {
      return this.currentStep > stepNumber || (this.finished && this.currentStep === stepNumber);
    },
    isStepClickable(stepNumber) {
      return this.clickableSteps && this.currentStep > stepNumber;
    },
    stepClass(stepNumber) {
      if (this.isStepComplete(stepNumber)) return 'ds-wizard__step--complete';
      if (this.currentStep === stepNumber) return 'ds-wizard__step--active';
      return 'ds-wizard__step--pending';
    },
  },
};
</script>

<style scoped>
.ds-wizard {
  display: flex;
  width: 100%;
  height: 88vh;
  min-height: 520px;
  max-height: 880px;
  overflow: hidden;
  background: var(--v-surface-base, #ffffff);
}

/* -------- Left rail -------- */
.ds-wizard__rail {
  width: 280px;
  min-width: 280px;
  background: linear-gradient(180deg, #0a2626 0%, #061a1a 50%, #021a1a 100%);
  display: flex;
  flex-direction: column;
  color: #fff;
}

.ds-wizard__brand {
  display: flex;
  align-items: center;
  gap: var(--ds-space-sm, 12px);
  padding: 24px 24px 16px;
}

.ds-wizard__brand-icon {
  width: 40px;
  height: 40px;
  border-radius: var(--ds-radius-md, 10px);
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ds-wizard__brand-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.ds-wizard__brand-title {
  font-family: var(--ds-font-family-heading);
  font-size: 16px;
  font-weight: 600;
}

.ds-wizard__brand-subtitle {
  font-size: 12px;
  opacity: 0.7;
  margin-top: 2px;
}

.ds-wizard__nav {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;
  overflow-y: auto;
}

.ds-wizard__step {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 24px;
  transition: background 0.2s ease;
  position: relative;
}

.ds-wizard__step--active {
  background: rgba(3, 98, 76, 0.2);
}

/* Opt-in (`clickable-steps`): a completed step is a button back to it. */
button.ds-wizard__step--clickable {
  width: 100%;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

button.ds-wizard__step--clickable:hover {
  background: rgba(255, 255, 255, 0.07);
}

button.ds-wizard__step--clickable:focus-visible {
  outline: 2px solid rgba(44, 194, 149, 0.6);
  outline-offset: -2px;
}

.ds-wizard__step--active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: var(--v-primary-base);
  border-radius: 0 2px 2px 0;
}

.ds-wizard__step--pending {
  opacity: 0.55;
}

.ds-wizard__step-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.ds-wizard__step--active .ds-wizard__step-circle {
  background: var(--v-primary-base);
  color: #fff;
}

/* Completed steps read as done in every wizard: success-green circle with a white check
   (both variants). The current step keeps its own look; upcoming steps are unchanged. */
.ds-wizard__step--complete .ds-wizard__step-circle,
.ds-wizard--pill .ds-wizard__step--complete .ds-wizard__step-circle {
  background: var(--v-success-base, #2cc295);
  color: #fff;
}

.ds-wizard__step-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.ds-wizard__step-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 2px;
}

.ds-wizard__step-desc {
  font-size: 12px;
  opacity: 0.7;
}

/* Active step only: lift the description to near-opaque so the 12px text clears AA
   (4.5:1) against the mint-tinted active row. Pending/disabled descriptions keep the
   muted 0.7 (their parent step is already dimmed and is WCAG-exempt as inactive). */
.ds-wizard__step--active .ds-wizard__step-desc {
  opacity: 0.92;
}

.ds-wizard__step-arrow {
  margin-left: auto;
  opacity: 0.85;
}

/* -------- Rail view-switch (segmented Config / Preview toggle) -------- */
.ds-wizard__viewswitch-wrap {
  /* Full-width divider mirrors the progress block below, so the switch never reads
     as a 4th step; the track's own margin insets the control beneath this line. */
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.ds-wizard__viewswitch {
  display: flex;
  gap: 4px;
  margin: 12px 24px 13px;
  padding: 4px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 10px;
}

.ds-wizard__viewbtn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 6px;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: rgba(255, 255, 255, 0.55);
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.ds-wizard__viewbtn:hover {
  color: rgba(255, 255, 255, 0.85);
}

.ds-wizard__viewbtn:focus-visible {
  outline: 2px solid rgba(44, 194, 149, 0.6);
  outline-offset: 1px;
}

.ds-wizard__viewbtn--active {
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
}

/* Icon follows the button's text colour (muted → brighter on hover); the active
   segment's icon is mint to tie into the progress-fill gradient. ::v-deep + the
   0,3,0 specificity is required to beat Vuetify's .theme--*.v-icon base colour. */
.ds-wizard__viewbtn ::v-deep .v-icon {
  color: inherit;
  transition: color 0.2s ease;
}

.ds-wizard__viewbtn--active ::v-deep .v-icon {
  color: #2cc295;
}

.ds-wizard__viewbtn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.ds-wizard__viewbtn:disabled:hover {
  color: rgba(255, 255, 255, 0.55);
}

/* -------- Optional rail extras (summary box / info rows) -------- */
.ds-wizard__extras {
  padding: 0 24px 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-shrink: 0;
}

/* -------- Optional rail progress -------- */
.ds-wizard__progress {
  padding: 12px 24px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}

.ds-wizard__viewswitch-wrap + .ds-wizard__progress {
  border-top: 0;
  padding-top: 4px;
}

.ds-wizard__progress-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  margin-bottom: 8px;
}

.ds-wizard__progress-row span:first-child {
  opacity: 0.7;
}

.ds-wizard__progress-value {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.ds-wizard__progress-track {
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  overflow: hidden;
}

.ds-wizard__progress-fill {
  height: 100%;
  width: 100%;
  background: linear-gradient(90deg, var(--ds-brand-castleton, #03624c), #9ab86e);
  border-radius: 2px;
  transform-origin: left center;
  transition: transform var(--ds-transition-slow, 0.3s ease);
}

/* -------- Optional 'pill' step variant (Append Data / Task Import look) -------- */
.ds-wizard--pill .ds-wizard__brand {
  padding-bottom: 24px;
}

.ds-wizard--pill .ds-wizard__brand-subtitle {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ds-wizard--pill .ds-wizard__nav {
  padding: 0 24px;
}

.ds-wizard--pill .ds-wizard__step {
  gap: 16px;
  padding: 12px;
  border-radius: var(--ds-radius-lg, 12px);
  transition: background 0.25s ease, box-shadow 0.25s ease, opacity 0.25s ease;
}

.ds-wizard--pill .ds-wizard__step--active {
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.ds-wizard--pill .ds-wizard__step--active::before {
  display: none;
}

.ds-wizard--pill .ds-wizard__step--pending {
  opacity: 0.6;
}

.ds-wizard--pill .ds-wizard__step--active .ds-wizard__step-circle {
  background: #fff;
  color: #0a2626;
  box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.2);
}

@media (prefers-reduced-motion: reduce) {
  .ds-wizard__progress-fill,
  .ds-wizard--pill .ds-wizard__step {
    transition: none;
  }
}

/* -------- Main content pane -------- */
.ds-wizard__main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  position: relative;
  background: var(--v-surface-base, #ffffff);
}

.ds-wizard__header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.08));
}

.ds-wizard__header-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.ds-wizard__header-title {
  font-family: var(--ds-font-family-heading);
  font-size: 1.375rem;
  font-weight: 600;
  line-height: 1.25;
}

.ds-wizard__header-subtitle {
  font-size: 0.875rem;
  /* Opacity (not a fixed colour) keeps this theme-adaptive: raising 0.6 -> 0.7 lifts the
     inherited body ink from ~4.30:1 to ~5.94:1 on the white pane (light) while also
     brightening it on the dark-pane variant. AA for 14px text needs 4.5:1. */
  opacity: 0.7;
  margin-top: 3px;
}

.ds-wizard__close {
  flex-shrink: 0;
}

.ds-wizard__content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  min-height: 0;
}

/* Flush: the consumer owns padding and scrolling (e.g. a pinned table that scrolls on its own). */
.ds-wizard__content--flush {
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.ds-wizard__footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 24px;
  border-top: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.08));
}

/* Dark theme: keep the rail as-is (dark by design); adapt the content pane. */
.theme--dark .ds-wizard__main {
  background: #1e1e1e;
}
</style>

<!-- Un-scoped: `content-class` lands on the teleported .v-dialog element (which also
     carries the inline max-width), so a scoped rule can't reach it. Animates the
     adaptive width when the consumer toggles views (e.g. Config ↔ Preview). -->
<style>
.ds-wizard-dialog {
  transition: max-width 0.22s cubic-bezier(0.22, 1, 0.36, 1);
}
</style>
